import type { HttpError } from "@refinedev/core";

/**
 * Normaliza el error de axios al shape `HttpError` que espera Refine
 * (`{ message, statusCode, errors? }`).
 *
 * Sin esto, el dataProvider re-lanza el `AxiosError` crudo: Refine no encuentra
 * `statusCode` y su notificación default queda en "(código de estado: )", y el
 * mensaje que mandó la API (ej. `El código de seller "x" ya existe en Magento`)
 * nunca llega al usuario — solo se ve "Request failed with status code 422".
 *
 * La API (NestJS) responde `{ message: string | string[], error, statusCode }`.
 */
export const toHttpError = (error: any): HttpError => {
  const response = error?.response;
  const data = response?.data;

  const rawMessage = data?.message ?? data?.error ?? error?.message;

  const message = Array.isArray(rawMessage)
    ? rawMessage.filter(Boolean).join(" · ")
    : typeof rawMessage === "string" && rawMessage.trim() !== ""
      ? rawMessage
      : "Ocurrió un error inesperado";

  const httpError: HttpError = {
    message,
    statusCode: Number(response?.status ?? data?.statusCode ?? 0),
  };

  // Errores de validación del ValidationPipe: llegan como array de strings.
  // Los dejamos accesibles por si una pantalla quiere mostrarlos por separado.
  if (Array.isArray(rawMessage) && rawMessage.length > 0) {
    (httpError as HttpError & { messages?: string[] }).messages =
      rawMessage.filter(Boolean).map(String);
  }

  return httpError;
};

/** Mensaje listo para mostrar a partir de cualquier error (axios o HttpError). */
export const getErrorMessage = (error: any): string => toHttpError(error).message;
